import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';
import { PureComponent } from 'react';
import Transition, { ENTERED } from 'react-transition-group/Transition';

import { Bem } from '../../service/Bem/Bem';
import { PopupScopeContext } from '../../service/Dom/Dom';
import { keyDownManager } from '../../service/KeyDownManager/KeyDownManager';
import { KeyCode } from '../../typings/common';
import { Alert, AlertType } from '../Alert/Alert';
import { AppModal } from '../AppModal/AppModal';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ContainerOverflow } from '../ContainerOverflow/ContainerOverflow';
import { Loader } from '../Loader/Loader';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './Inspector.css';
import * as keyset from './Inspector.i18n';

const i18n = i18nFactory(keyset);

type InspectorContents = React.ReactNode | string;

export interface InspectorProps {
    visible: boolean;
    progress: boolean;
    cls?: string;
    header?: InspectorContents;
    footer?: InspectorContents;
    hint?: InspectorContents;
    error?: InspectorContents;
    hasClose?: boolean;
    hasCancel?: boolean;
    hasOverlay?: boolean;
    isCloseDisabled?: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onEsc?: () => void;
    onErrorClose?: () => void;
}

const BLOCK = 'inspector';

export class Inspector extends PureComponent<InspectorProps> {
    public static defaultProps: Partial<InspectorProps> = {
        hasClose: true
    };

    private keyDownManagerId: string;

    render() {
        return <AppModal>{this.getContent()}</AppModal>;
    }

    getContent() {
        const { cls, visible, hasClose } = this.props;

        return (
            <>
                <Transition in={visible} timeout={300} mountOnEnter={true} unmountOnExit={true}>
                    {(state: string) => {
                        return (
                            <div
                                className={classNames(
                                    cls,
                                    Bem.cls({
                                        block: BLOCK,
                                        mods: {
                                            visible: visible && state === ENTERED,
                                            'has-close': Boolean(hasClose),
                                            [state]: true
                                        }
                                    })
                                )}
                            >
                                <PopupScopeContext.Provider value={this.getContentWrapperRef}>
                                    <div className={Bem.cls(BLOCK, 'wrapper')}>
                                        {this.getHeader()}
                                        <div className={Bem.cls(BLOCK, 'body')}>
                                            <ContainerOverflow>{this.props.children}</ContainerOverflow>
                                        </div>
                                        {this.getError()}
                                        {this.getSpinner()}
                                    </div>
                                    {this.getFooter()}
                                    {this.getClose()}
                                </PopupScopeContext.Provider>
                            </div>
                        );
                    }}
                </Transition>

                {this.getOverlay()}
            </>
        );
    }

    componentDidUpdate(prevProps: InspectorProps) {
        const { visible, isCloseDisabled } = this.props;

        if (this.keyDownManagerId && (visible !== prevProps.visible || isCloseDisabled !== prevProps.isCloseDisabled)) {
            keyDownManager.turn(this.keyDownManagerId, visible && !isCloseDisabled);
        }
    }

    componentDidMount() {
        if (this.props.onEsc) {
            this.keyDownManagerId = keyDownManager.on([[KeyCode.ESC, this.props.onEsc]], false);
        }
    }

    componentWillUnmount() {
        if (this.keyDownManagerId) {
            keyDownManager.off(this.keyDownManagerId);
        }
    }

    private getSpinner() {
        const { progress } = this.props;

        if (!progress) {
            return null;
        }

        return <Loader size={'l'} />;
    }

    private getFooter() {
        const { footer, hasCancel, onCancel, isCloseDisabled } = this.props;

        if (!footer && (!hasCancel || !onCancel)) {
            return null;
        }

        const cancel = hasCancel ? (
            <ButtonDecorator
                disabled={isCloseDisabled}
                tone="default"
                view="default"
                theme={'normal'}
                text={i18n('cancel')}
                key={'cancel-btn'}
                onClick={onCancel}
                size={'n'}
            />
        ) : (
            ''
        );

        return (
            <div
                className={classNames(
                    Bem.cls(BLOCK, 'footer'),
                    Bem.cls({
                        block: BLOCK,
                        elem: 'footer',
                        mods: { 'has-cancel': hasCancel || false }
                    })
                )}
            >
                {footer}
                {cancel}
            </div>
        );
    }

    private getClose() {
        const { hasClose, onClose, isCloseDisabled } = this.props;
        return hasClose ? (
            <div
                className={Bem.cls(BLOCK, 'close', {
                    disabled: Boolean(isCloseDisabled)
                })}
                onClick={() => {
                    if (!isCloseDisabled && onClose !== undefined) {
                        onClose();
                    }
                }}
            >
                <Shape2 width={16} height={16} icon={Shape2Icon.close} />
            </div>
        ) : null;
    }

    private getHeader() {
        const { header } = this.props;

        if (!header) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'header')}>
                <div className={Bem.cls(BLOCK, 'title')}>{header}</div>
                {this.getPopupHint()}
            </div>
        );
    }

    private getPopupHint() {
        const { hint } = this.props;

        if (!hint) {
            return null;
        }

        return <div className={Bem.cls(BLOCK, 'header-hint')}>{hint}</div>;
    }

    private getError() {
        const { error, onErrorClose } = this.props;

        if (!error) {
            return null;
        }

        return (
            <Alert
                className={Bem.cls({
                    block: BLOCK,
                    elem: 'error'
                })}
                onClose={onErrorClose}
                type={AlertType.error}
            >
                {error}
            </Alert>
        );
    }

    private getOverlay() {
        const { visible, hasOverlay } = this.props;

        if (!hasOverlay) {
            return;
        }

        return (
            <div
                className={Bem.cls(BLOCK, 'overlay', {
                    visible
                })}
            />
        );
    }

    private getContentWrapperRef() {
        return document.body;
    }
}
